/**
 * Corporate Access Hook & Provider
 *
 * Resolves per-user corporate visibility and access levels from the
 * `user_corporate_access` table. Admins bypass all checks and receive
 * implicit "owner" access to every corporate.
 *
 * Key concepts:
 *  - **Portal User**: a non-admin, non-manager user with at least one
 *    corporate access row. Portal users are redirected to their primary
 *    corporate dashboard and cannot see the global platform sidebar.
 *  - **Access Levels**: owner | editor | viewer — queried once on mount
 *    and cached via React Query.
 *  - `hasCorporateAccess(id)` — gate for route guards and data fetching.
 *  - `getCorporateAccessLevel(id)` — used to conditionally render edit
 *    controls or restrict mutations.
 */

import { createContext, useContext, useMemo, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type AccessLevel = "owner" | "editor" | "viewer";
type EntityType = "platform" | "issuing_bank" | "corporate" | "supplier";

interface EntityAccessEntry {
  corporateId: number;
  accessLevel: AccessLevel;
  entityType: EntityType;
}

interface CorporateAccessContextType {
  corporateIds: number[];
  accessMap: Map<number, AccessLevel>;
  entityAccessList: EntityAccessEntry[];
  loading: boolean;
  isPortalUser: boolean;
  primaryCorporateId: number | null;
  hasCorporateAccess: (corporateId: number) => boolean;
  getCorporateAccessLevel: (corporateId: number) => AccessLevel | null;
  getEntityAccess: (entityId: number, entityType: EntityType) => AccessLevel | null;
  hasEntityAccess: (entityId: number, entityType: EntityType) => boolean;
}

const CorporateAccessContext = createContext<CorporateAccessContextType | undefined>(undefined);

/** Corporate Access Provider component. */
export function CorporateAccessProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading, hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["user-corporate-access", user?.id, isAdmin],
    queryFn: async () => {
      if (isAdmin) return [];
      const { data, error } = await supabase
        .from("user_corporate_access")
        .select("corporate_id, access_level, entity_type")
        .eq("user_id", user!.id)
        // Deterministic order: `primaryCorporateId` below is derived from
        // rows[0]. Without an explicit order, Postgres/PostgREST give no
        // guarantee about row order across requests, so a portal user with
        // access to multiple corporates could get a different
        // "primary" corporate on different loads (adversarial review,
        // R19/R20 follow-up).
        .order("corporate_id", { ascending: true });
      if (error) throw error;
      return data as { corporate_id: number; access_level: AccessLevel; entity_type: EntityType }[];
    },
    enabled: !!user,
  });

  const isManager = hasRole("manager");
  const isPortalUser = !isAdmin && !isManager && rows.length > 0;
  const primaryCorporateId = isPortalUser ? rows[0]?.corporate_id ?? null : null;

  const value = useMemo<CorporateAccessContextType>(() => {
    const accessMap = new Map<number, AccessLevel>();
    const corporateIds: number[] = [];
    const entityAccessList: EntityAccessEntry[] = [];

    for (const row of rows) {
      accessMap.set(row.corporate_id, row.access_level as AccessLevel);
      corporateIds.push(row.corporate_id);
      entityAccessList.push({
        corporateId: row.corporate_id,
        accessLevel: row.access_level as AccessLevel,
        entityType: (row.entity_type ?? "corporate") as EntityType,
      });
    }

    return {
      corporateIds,
      accessMap,
      entityAccessList,
      loading: authLoading || (!isAdmin && isLoading),
      isPortalUser,
      primaryCorporateId,
      hasCorporateAccess: (id: number) => isAdmin || accessMap.has(id),
      getCorporateAccessLevel: (id: number) =>
        isAdmin ? "owner" : (accessMap.get(id) ?? null),
      getEntityAccess: (entityId: number, entityType: EntityType) => {
        if (isAdmin) return "owner";
        const entry = entityAccessList.find(
          (e) => e.corporateId === entityId && e.entityType === entityType
        );
        if (entry) return entry.accessLevel;
        // Issuer-level access implies buyer visibility
        if (entityType === "corporate") {
          const hasIssuerAccess = entityAccessList.some(
            (e) => e.entityType === "issuing_bank"
          );
          if (hasIssuerAccess) return "viewer";
        }
        return null;
      },
      hasEntityAccess: (entityId: number, entityType: EntityType) => {
        if (isAdmin) return true;
        const entry = entityAccessList.find(
          (e) => e.corporateId === entityId && e.entityType === entityType
        );
        if (entry) return true;
        if (entityType === "corporate") {
          return entityAccessList.some((e) => e.entityType === "issuing_bank");
        }
        return false;
      },
    };
  }, [rows, isAdmin, authLoading, isLoading, isPortalUser, primaryCorporateId]);

  return (
    <CorporateAccessContext.Provider value={value}>
      {children}
    </CorporateAccessContext.Provider>
  );
}

/** Manages corporate access state and operations. */
export function useCorporateAccess() {
  const context = useContext(CorporateAccessContext);
  if (!context) {
    throw new Error("useCorporateAccess must be used within CorporateAccessProvider");
  }
  return context;
}
